// TeamFilter.jsx - Complete rewrite for stability
import React, { useCallback, useMemo, memo } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Typography,
  Switch,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Chip,
  alpha,
} from "@mui/material";
import {
  Check as CheckIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  BookmarkBorder as BookmarkIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";

// Styled Components
const TeamCard = styled(Paper)(({ theme }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

const TeamHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  cursor: "pointer",
}));

const MemberListContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const MemberItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 4,
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

const StatusChip = styled(Chip)(({ theme, color = "primary" }) => ({
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  backgroundColor: alpha(theme.palette[color].main, 0.12),
  color: theme.palette[color].main,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

// Helper functions (outside component to avoid recreating them)
const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

/**
 * TeamFilter Component - Displays teams and members with toggle switches
 */
const TeamFilter = memo(
  ({
    teams,
    expandedTeams,
    visibleMembers,
    activeTeamId,
    handleTeamToggle,
    handleMemberToggle,
    theme,
  }) => {
    // Pre-process and memoize the member data for better performance
    const processedTeams = useMemo(() => {
      return teams.map((team) => {
        // Get all active members
        const filteredMembers = team.members
          .filter((member) => member.status === "active")
          .map((member) => {
            // Extract member details
            const memberId = member.userId || member.id;

            // Skip invalid members
            if (!memberId) return null;

            // Get display name
            let displayName = "";
            if (member.user?.name) displayName = member.user.name;
            else if (member.name) displayName = member.name;
            else if (member.email) displayName = member.email.split("@")[0];
            else displayName = "Unknown";

            // Determine role information
            let roleInfo = {
              text: "Member",
              icon: PersonIcon,
              color: theme.palette.primary.main,
            };

            if (member.role === "admin") {
              roleInfo = {
                text: "Admin",
                icon: AdminIcon,
                color: theme.palette.warning.main,
              };
            } else if (!member.userId) {
              roleInfo = {
                text: "Client",
                icon: BusinessIcon,
                color: theme.palette.info.main,
              };
            }

            return {
              originalMember: member,
              id: memberId,
              displayName,
              roleInfo,
              isVisible: !!visibleMembers[memberId],
            };
          })
          .filter(Boolean); // Remove null entries

        return {
          ...team,
          filteredMembers,
        };
      });
    }, [teams, visibleMembers, theme]);

    // Render a single team member item
    const renderMemberItem = useCallback(
      (member, teamId) => {
        const RoleIcon = member.roleInfo.icon;

        return (
          <MemberItem key={`${teamId}-${member.id}`} disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: alpha(member.roleInfo.color, 0.1),
                  color: member.roleInfo.color,
                }}
              >
                {getInitials(member.displayName)}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" noWrap>
                  {member.displayName}
                </Typography>
              }
            />
            <Switch
              checked={member.isVisible}
              onChange={(e) => {
                e.stopPropagation();
                handleMemberToggle(member.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
            />
          </MemberItem>
        );
      },
      [handleMemberToggle]
    );

    // Render function
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: { xs: 2, md: 0 },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          pb={1.5}
          borderBottom={`1px solid ${theme.palette.divider}`}
        >
          <GroupsIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>
            Teams
          </Typography>
        </Box>

        {/* Status indicators */}
        <Box display="flex" justifyContent="space-between" mb={2}>
          <StatusChip
            label="Available"
            color="success"
            size="small"
            icon={<CheckIcon />}
          />
          <StatusChip
            label="Booked"
            color="error"
            size="small"
            icon={<BookmarkIcon />}
          />
        </Box>

        {/* Teams list */}
        {processedTeams.map((team) => (
          <TeamCard
            key={team.id}
            elevation={0}
            sx={{
              border:
                activeTeamId === team.id
                  ? `2px solid ${theme.palette.primary.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* Team header */}
            <TeamHeader onClick={() => handleTeamToggle(team.id)}>
              <Box display="flex" alignItems="center">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  {getInitials(team.name)}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600}>
                  {team.name}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Badge
                  badgeContent={team.filteredMembers.length}
                  color="primary"
                  sx={{ mr: 1.5 }}
                >
                  <PersonIcon fontSize="small" color="action" />
                </Badge>
                {expandedTeams[team.id] ? (
                  <ExpandLessIcon fontSize="small" color="action" />
                ) : (
                  <ExpandMoreIcon fontSize="small" color="action" />
                )}
              </Box>
            </TeamHeader>

            {/* Team members list */}
            {expandedTeams[team.id] && (
              <MemberListContainer>
                {team.filteredMembers.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                    sx={{ py: 2 }}
                  >
                    No members match the current filter
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {team.filteredMembers.map((member) =>
                      renderMemberItem(member, team.id)
                    )}
                  </List>
                )}
              </MemberListContainer>
            )}
          </TeamCard>
        ))}
      </Paper>
    );
  }
);

export default TeamFilter;
